/**
 * Customer Portal (UI) - filter state management.
 *
 * Campaign and phone scope stay in one place so each dashboard variant can use
 * the same aggregate-first filtering semantics.
 */
import { useMemo, useState } from 'react';
import type { PortalFilters } from '../../types/portal';

export type FilterStore = {
  filters: PortalFilters;
  setBrandId: (brandId: string | null) => void;
  setCampaignId: (campaignId: string | null) => void;
  setPhoneNumberId: (phoneNumberId: string | null) => void;
  resetFilters: () => void;
};

export function useFilterStore(): FilterStore {
  const [filters, setFilters] = useState<PortalFilters>({
    brandId: null,
    campaignId: null,
    phoneNumberId: null
  });

  return useMemo(
    () => ({
      filters,
      setBrandId: (brandId: string | null) =>
        setFilters((current) => ({
          ...current,
          brandId,
          // Selecting a different brand invalidates lower scopes.
          campaignId: brandId === current.brandId ? current.campaignId : null,
          phoneNumberId: brandId === current.brandId ? current.phoneNumberId : null
        })),
      setCampaignId: (campaignId: string | null) =>
        setFilters((current) => ({
          brandId: current.brandId,
          campaignId,
          // Clearing campaign should also clear phone because phone scope
          // is campaign-dependent.
          phoneNumberId: campaignId ? current.phoneNumberId : null
        })),
      setPhoneNumberId: (phoneNumberId: string | null) =>
        setFilters((current) => ({
          ...current,
          phoneNumberId
        })),
      resetFilters: () =>
        setFilters({
          brandId: null,
          campaignId: null,
          phoneNumberId: null
        })
    }),
    [filters]
  );
}
